import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { checkHRSubscription } from '../utils/subscriptionUtils.js';

// Cache the latest subscription payload per-user to prevent UI flicker
// when moving between pages that re-mount the hook (e.g., HR guarded routes).
let cachedSubscription = null;
let cachedHRStatus = undefined;
let cachedUserId = null;

export const useSubscription = () => {
  const { user } = useAuth();
  const isSameUser = user?.id && user.id === cachedUserId;

  // Seed state from cache when available to avoid transient "no subscription" states.
  const [subscription, setSubscription] = useState(() => (isSameUser ? cachedSubscription : null));
  const [loading, setLoading] = useState(() => !isSameUser);
  const [hrSubscriptionStatus, setHRSubscriptionStatus] = useState(() => (isSameUser ? cachedHRStatus : undefined));

  const fetchSubscription = async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const db = getFirestore(firebaseApp);
      const collectionName = user.userType === 'hr' ? 'recruiters' : 'jobseekers';
      const userDoc = await getDoc(doc(db, collectionName, user.id));

      if (userDoc.exists()) {
        const data = userDoc.data();

        // For HR users, check new subscription model
        if (user.userType === 'hr') {
          const hrStatus = await checkHRSubscription(user.id);
          setHRSubscriptionStatus(hrStatus);
          cachedHRStatus = hrStatus;

          // Check NEW subscription object format first, then fall back to old format
          const subData = data.subscription;

          if (subData && subData.status === 'active' && subData.plan !== 'none') {
            // New subscription format
            const expiresAt = subData.expiresAt?.toDate
              ? subData.expiresAt.toDate()
              : (subData.expiresAt ? new Date(subData.expiresAt) : null);
            const startDate = subData.startDate?.toDate
              ? subData.startDate.toDate()
              : (subData.startDate ? new Date(subData.startDate) : null);

            const nextSubscription = {
              plan: subData.plan,
              status: 'active',
              startDate: startDate,
              expiresAt: expiresAt,
            };
            setSubscription(nextSubscription);
            cachedSubscription = nextSubscription;
            cachedUserId = user.id;
            console.log('✅ HR Subscription loaded (new format):', subData.plan);
          } else if (hrStatus.hasSubscription && hrStatus.isActive) {
            // Fallback to old format
            const planName = data.subscriptionPlan?.toLowerCase() || 'none';
            const nextSubscription = {
              plan: planName,
              status: 'active',
              startDate: data.startDate ? new Date(data.startDate) : null,
              expiresAt: data.endDate ? new Date(data.endDate) : null,
            };
            setSubscription(nextSubscription);
            cachedSubscription = nextSubscription;
            cachedUserId = user.id;
            console.log('✅ HR Subscription loaded (old format):', planName);
          } else {
            const nextSubscription = {
              plan: 'none',
              status: hrStatus.isExpired ? 'expired' : 'inactive',
              startDate: null,
              expiresAt: null,
            };
            setSubscription(nextSubscription);
            cachedSubscription = nextSubscription;
            cachedUserId = user.id;
            console.log('⚠️ HR has no active subscription');
          }
        } else {
          // For job seekers, use old subscription model
          const subscriptionData = data.subscription;

          console.log('📊 Job Seeker Subscription Data:', subscriptionData);

          if (subscriptionData) {
            const parsedSubscription = {
              plan: subscriptionData.plan,
              status: subscriptionData.status,
              startDate: subscriptionData.startDate?.toDate ? subscriptionData.startDate.toDate() : null,
              expiresAt: subscriptionData.expiresAt?.toDate ? subscriptionData.expiresAt.toDate() : null,
            };

            console.log('✅ Parsed Subscription:', parsedSubscription);
            console.log('🔍 isPro will be:', parsedSubscription.plan === 'pro' && parsedSubscription.status === 'active');
            setSubscription(parsedSubscription);
            cachedSubscription = parsedSubscription;
            cachedUserId = user.id;
          } else {
            // FALLBACK: Default subscription for old users without subscription field
            const defaultSubscription = {
              plan: 'free',
              status: 'active',
              startDate: null,
              expiresAt: null,
            };

            console.log('⚠️ User missing subscription field, using default:', defaultSubscription);
            setSubscription(defaultSubscription);
            cachedSubscription = defaultSubscription;
            cachedUserId = user.id;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // Default to free plan on error
      const fallback = {
        plan: user?.userType === 'jobseeker' ? 'free' : 'none',
        status: 'active',
        startDate: null,
        expiresAt: null,
      };
      setSubscription(fallback);
      if (user?.id) {
        cachedSubscription = fallback;
        cachedUserId = user.id;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If the user changes, clear stale cache and start a fresh fetch.
    if (!isSameUser) {
      cachedSubscription = null;
      cachedHRStatus = undefined;
      cachedUserId = user?.id || null;
    }

    fetchSubscription();

    // Re-check subscription every 5 minutes for HR users
    if (user?.userType === 'hr') {
      const interval = setInterval(fetchSubscription, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  const isPro = subscription?.plan === 'pro' && subscription?.status === 'active';
  const isCore = subscription?.plan === 'core' && subscription?.status === 'active';
  const isNexora = subscription?.plan === 'nexora' && subscription?.status === 'active';
  const isEnterprise = subscription?.plan === 'enterprise' && subscription?.status === 'active';

  // For HR users, need both active status AND a valid plan (not 'none')
  const hasActiveSubscription = subscription?.status === 'active' && subscription?.plan !== 'none';
  const needsSubscription = user?.userType === 'hr' && (!subscription || subscription.plan === 'none' || subscription.status !== 'active');

  return {
    subscription,
    isPro,
    isCore,
    isNexora,
    isEnterprise,
    loading,
    refreshSubscription: fetchSubscription,
    hasActiveSubscription,
    needsSubscription,
    subscriptionStatus: hrSubscriptionStatus,
  };
};

